import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { DSCard, DSInput } from '../design-system/primitives';
import './KnowledgeBase.css';

function KnowledgeBaseHome() {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isActive = true;

    const fetchKnowledgeBase = async () => {
      try {
        setLoading(true);
        const response = await apiService.getKnowledgeBaseHome();
        if (!isActive) {
          return;
        }
        setCategories(response.data.categories || []);
        setArticles(response.data.articles || []);
        setFeatured(response.data.featured || []);
        setError(null);
      } catch (err) {
        if (!isActive) {
          return;
        }
        setError('Unable to load help articles.');
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchKnowledgeBase();
    return () => {
      isActive = false;
    };
  }, []);

  const filteredArticles = useMemo(() => {
    if (!query.trim()) {
      return [];
    }
    const lowered = query.toLowerCase();
    return articles.filter((article) => (
      article.title?.toLowerCase().includes(lowered) ||
      article.excerpt?.toLowerCase().includes(lowered)
    ));
  }, [articles, query]);

  const suggestions = filteredArticles.slice(0, 6);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (suggestions.length > 0) {
      navigate(`/help/article/${suggestions[0].slug}`);
    }
  };

  if (loading) {
    return (
      <div className="kb-page">
        <div className="kb-content">
          <DSCard className="kb-hero" cornerVariant="lg">
            <h1>Help Center</h1>
            <p>Loading help articles...</p>
          </DSCard>
        </div>
      </div>
    );
  }

  return (
    <div className="kb-page">
      <div className="kb-content">
        <DSCard className="kb-hero" cornerVariant="lg">
          <h1>Help Center</h1>
          <p>Find answers fast, or browse by category.</p>
          <form className="kb-search" onSubmit={handleSubmit}>
            <DSInput
              type="search"
              placeholder="Search help articles"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Search help articles"
            />
            {query.trim().length > 0 && suggestions.length > 0 && (
              <div className="kb-suggestions">
                {suggestions.map((article) => (
                  <Link
                    key={article.id}
                    to={`/help/article/${article.slug}`}
                    className="kb-suggestion"
                  >
                    {article.title}
                  </Link>
                ))}
              </div>
            )}
          </form>
          {query.trim().length > 0 && (
            <div className="kb-search-results">
              {filteredArticles.length === 0 ? (
                <DSCard className="kb-empty" cornerVariant="md">No articles match that search yet.</DSCard>
              ) : (
                filteredArticles.slice(0, 12).map((article) => (
                  <Link
                    key={article.id}
                    to={`/help/article/${article.slug}`}
                    className="kb-article-item"
                  >
                    <h3>{article.title}</h3>
                    <div
                      className="kb-excerpt"
                      dangerouslySetInnerHTML={{ __html: article.excerpt || '' }}
                    />
                  </Link>
                ))
              )}
            </div>
          )}
          {error && <DSCard className="kb-empty" cornerVariant="md">{error}</DSCard>}
        </DSCard>

        <section className="kb-section">
          <h2>Browse categories</h2>
          <div className="kb-card-grid">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/help/category/${category.slug}`}
                className="kb-card"
              >
                <span className="kb-card-icon" aria-hidden="true">
                  {(category.name || '?').charAt(0).toUpperCase()}
                </span>
                <div className="kb-card-content">
                  <h3>{category.name}</h3>
                  <p>{category.description || `${category.article_count || 0} articles`}</p>
                </div>
                <span className="kb-card-arrow" aria-hidden="true">&gt;</span>
              </Link>
            ))}
            {categories.length === 0 && (
              <DSCard className="kb-empty" cornerVariant="md">No categories are available yet.</DSCard>
            )}
          </div>
        </section>

        <section className="kb-section">
          <h2>Featured articles</h2>
          <div className="kb-article-list">
            {featured.map((article) => (
              <Link
                key={article.id}
                to={`/help/article/${article.slug}`}
                className="kb-article-item"
              >
                <h3>{article.title}</h3>
                <div
                  className="kb-excerpt"
                  dangerouslySetInnerHTML={{ __html: article.excerpt || '' }}
                />
              </Link>
            ))}
            {featured.length === 0 && (
              <DSCard className="kb-empty" cornerVariant="md">No featured articles yet.</DSCard>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default KnowledgeBaseHome;
